/** The images an operator attached to a message: thumbnails that open full size in a
 * dialog, so viewing one does not leave the conversation. */
import { useEffect, useRef, useState } from "react";

import type { RunMessageImage } from "../api/hooks/useStatefulAgents";
import styles from "./MessageImages.module.css";

export const MessageImages = ({ images }: { images: RunMessageImage[] }) => {
  const [open, setOpen] = useState<RunMessageImage | null>(null);
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <div className={styles.images}>
      {images.map((image, n) =>
        image.url ? (
          <button
            key={n}
            type="button"
            className={styles.thumb}
            title={image.name}
            onClick={() => setOpen(image)}
          >
            <img src={image.url} alt={image.name} />
          </button>
        ) : (
          // No browser-viewable copy yet (a HEIC before the agent has read it).
          <span key={n} className={styles.name}>
            {image.name}
          </span>
        ),
      )}
      <dialog
        ref={ref}
        className={styles.dialog}
        onClose={() => setOpen(null)}
        // A click on the backdrop lands on the dialog itself, not its content.
        onClick={(event) => {
          if (event.target === event.currentTarget) setOpen(null);
        }}
      >
        {open?.url && (
          <>
            <div className={styles.bar}>
              <span className={styles.title}>{open.name}</span>
              <a href={open.url} target="_blank" rel="noreferrer">
                Open in new tab
              </a>
              <button type="button" className={styles.close} onClick={() => setOpen(null)}>
                Close
              </button>
            </div>
            <img className={styles.full} src={open.url} alt={open.name} />
          </>
        )}
      </dialog>
    </div>
  );
};
